<script setup>
import router from '@/router';
import { useMutation } from '@tanstack/vue-query';
import { API } from '@/services/index';
import { RESET_USER_PASSWORD } from '@/services/auth/mutations/index.js';
import Cookies from 'js-cookie';
import { useUserStore } from '@/stores/user.store.js';
import { useAuthStore } from '@/stores/auth.store.js';

// .matches(/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,10}$/, 'email must be a valid email'),

const formRules = {
  email: (value) => !!value || 'Email is required',
  password: (value) => !!value || 'Password is required',
  password_confirmation: (value) => !!value || 'Password confirmation is required',
  passwordRegex: (value) =>
    useUserStore().passwordRegex.test(value) ||
    'Password must be a minimum of eight characters, at least one uppercase letter, one lowercase letter, one number and one special character'
};

const props = defineProps({
  token: {
    type: String,
    required: true
  },
  tokenEmail: {
    type: String,
    required: true
  }
});

const route = useRoute();
const resetForm = ref();
const isFormValid = ref(false);
const email = ref(props.tokenEmail);
const password = ref(null);
const passwordConfirmation = ref(null);
const isPasswordVisible = ref(false);
const isPasswordConfirmVisible = ref(false);
const isPasswordUpdated = ref(false);
const isLoading = ref(false);
const resetErrors = ref(null);
const inviteToken = route.query?.invite_token ?? null;

const resetPasswordMutation = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql', RESET_USER_PASSWORD, payload);
  },
  onSuccess: (data) => {
    if (data.data.data.resetPassword) {
      isPasswordUpdated.value = true;
      useAuthStore().login(data.data.data.resetPassword.token);
      //Cookies.set('_pup_access_token', data.data.data.resetPassword.token, {
      //  expires: 7,
      //  sameSite: 'strict',
      //  secure: true
      //});

      if (data.data.data.resetPassword.new_user) {
        localStorage.setItem('_pup_new_user', true);
        router.push('/onboard');
      } else {
        // user was probably imported from old backstage. Just send them to the dashboard.
        router.push('/');
      }
      isLoading.value = false;
    }

    if (data.data.errors) {
      // Helpers.showError(data.data.error.message);
      console.log(data.data.errors);
      resetErrors.value = data.data.errors[0]?.message != 'validation' ? data.data.errors[0]?.message : data.data.errors[0]?.extensions;
      isLoading.value = false;
    }
  },
  onError: (error) => {
    console.log('error', error);
    isLoading.value = false;
  }
});

const onSubmitHandler = () => {
  isLoading.value = true;
  resetPasswordMutation.mutateAsync({
    token: props.token,
    email: email.value,
    password: password.value,
    password_confirmation: passwordConfirmation.value,
    invite_token: inviteToken
  });
};
</script>

<template>
  <div>
    <div v-if="resetErrors" class="tw-mt-2 tw-text-center tw-text-xs tw-font-bold tw-text-red-700">
      <template v-if="resetErrors.validation">
        <div v-for="(messages, field) in resetErrors.validation" :key="field">
          <div v-for="(msg, i) in messages" :key="i">{{ msg }}</div>
        </div>
      </template>
      <template v-else-if="typeof resetErrors === 'string'">{{ resetErrors }}</template>
    </div>
    <div v-if="isPasswordUpdated" class="tw-mt-5 tw-text-center tw-text-[0.875rem] tw-font-semibold tw-text-green-700">
      Your password has been successfully updated.
    </div>
    <v-form class="mt-5" ref="resetForm" v-model="isFormValid" @submit.prevent="onSubmitHandler">
      <div class="tw-flex tw-w-full tw-flex-col tw-space-y-3">
        <div class="tw-flex tw-w-full tw-flex-col">
          <v-text-field
            v-model="email"
            :rules="[formRules.email]"
            variant="outlined"
            color="primary-blue"
            placeholder="Email Address*"
            hide-details="auto"
            density="comfortable"
            :disabled="isLoading"
          >
          </v-text-field>
        </div>
        <div class="tw-flex tw-w-full tw-flex-col">
          <v-text-field
            v-model="password"
            :type="isPasswordVisible ? 'text' : 'password'"
            :rules="[formRules.password, formRules.passwordRegex]"
            variant="outlined"
            color="primary-blue"
            placeholder="Password*"
            hide-details="auto"
            density="comfortable"
            :disabled="isLoading"
          >
            <template v-slot:append-inner>
              <v-icon color="primary-blue" @click="isPasswordVisible = !isPasswordVisible" :style="password ? 'opacity: 100%' : ''">
                {{ isPasswordVisible ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </template>
          </v-text-field>
        </div>
        <div class="tw-flex tw-w-full tw-flex-col">
          <v-text-field
            v-model="passwordConfirmation"
            :type="isPasswordConfirmVisible ? 'text' : 'password'"
            :rules="[formRules.password_confirmation, formRules.passwordRegex]"
            variant="outlined"
            color="primary-blue"
            placeholder="Password Confirm*"
            hide-details="auto"
            density="comfortable"
            :disabled="isLoading"
          >
            <template v-slot:append-inner>
              <v-icon
                color="primary-blue"
                @click="isPasswordConfirmVisible = !isPasswordConfirmVisible"
                :style="passwordConfirmation ? 'opacity: 100%' : ''"
              >
                {{ isPasswordConfirmVisible ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </template>
          </v-text-field>
        </div>
      </div>
      <div class="tw-mt-2 tw-flex tw-w-full">
        <v-btn
          color="primary-blue"
          size="large"
          rounded="lg"
          block
          flat
          class="tw-font-bold tw-capitalize"
          style="letter-spacing: 0px"
          type="submit"
          :loading="isLoading"
          :disabled="isLoading || !isFormValid"
        >
          Update Password
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<style lang="css" scoped></style>
